'use client';

import { useState, useEffect, useCallback } from 'react';

const CD_COOKIE = 'dyste_cd_seen';
const CD_DURATION = 3200; // how long CD spins before transitioning out
const CD_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

// null  = hydration pending  (render overlay to block content)
// true  = show the CD        (render overlay + CD)
// false = skip               (render nothing)
type CDState = null | boolean;

export default function CDSplash() {
  // Start as null so the blocking overlay renders on first paint
  const [cdState, setCdState] = useState<CDState>(null);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    // After hydration, decide instantly — no setTimeout delay
    try {
      const ts = localStorage.getItem(CD_COOKIE);
      if (ts && Date.now() - parseInt(ts, 10) < CD_EXPIRY_MS) {
        // Already seen — dismiss immediately, no animation
        setCdState(false);
        return;
      }
    } catch { /* localStorage blocked — show it */ }
    setCdState(true);
  }, []);

  const dismiss = useCallback(() => {
    setExiting(true);
    try { localStorage.setItem(CD_COOKIE, String(Date.now())); } catch { }
    setTimeout(() => setCdState(false), 800); // matches exit animation
  }, []);

  // Auto-dismiss after CD_DURATION
  useEffect(() => {
    if (cdState !== true || exiting) return;
    const t = setTimeout(dismiss, CD_DURATION);
    return () => clearTimeout(t);
  }, [cdState, exiting, dismiss]);

  // false = user already visited, skip entirely
  if (cdState === false) return null;

  // null (pending) or true (active) — render the overlay
  // When pending (null), render without the spinning disc animation so it's
  // just a bare dark cover that disappears instantly if not needed.
  const isPending = cdState === null;

  return (
    <div
      className={`cd-splash${exiting ? ' cd-exit' : ''}${isPending ? ' cd-pending' : ''}`}
      onClick={isPending ? undefined : dismiss}
      onKeyDown={(e) => { if (!isPending && (e.key === 'Enter' || e.key === ' ')) dismiss(); }}
      tabIndex={isPending ? -1 : 0}
      role="dialog"
      aria-modal="true"
      aria-label="Loading screen"
    >
      {!isPending && (
        <>
          <div className="cd-disc">
            <div className="cd-ring" />
            <div className="cd-label">
              <span className="cd-logo">dyste.</span>
            </div>
         
          </div>
        </>
      )}
    </div>
  );
}
